import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable, { DataTableColumn } from '../DataTable';
import RowActionsDropdown, { RowAction } from '../RowActionsDropdown';
import {
  useDeleteStyleMutation,
  useGetAllModelsQuery,
  useGetAllStylesQuery,
  useRestoreStyleMutation,
} from '../tools.service';
import { StyleListItemType } from '../tools.types';
import routes from '../../../routes';
import { formatAiProviderName } from '../../../utils/aiProvider';
import { formatToolIdName, TOOL_IDS } from '../../../utils/toolId';
import { isFetchBaseQueryError } from '../../../utils/helpers';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useDebounce } from '../../../hooks/useDebounce';
import { usePagedList } from '../../../hooks/usePagedList';
import PaginationBar from '../../../UIComponents/PaginationBar';
import { useAppDispatch } from '../../../hooks/defaultHooks';
import { openModal } from '../../../store/slices/modalSlice';
import cls from './StylesTab.module.scss';

/** One wide page is enough to pick a model in the filter */
const MODELS_FILTER_SIZE = 100;

const DASH = '-';

/**
 * The row carries a snapshot of one prompt: the draft if there is one, otherwise the newest
 * published version. A label whose prompts are all gone comes back with that half empty, and the
 * column says so - that is what tells the admin the style needs a prompt.
 */
const formatSnapshotVersion = (style: StyleListItemType) => {
  if (!style.prompt) return 'No prompt';
  return style.version === null || style.version === undefined ? 'Draft' : `v${style.version}`;
};

const StylesTab = () => {
  const [toolId, setToolId] = useState('');
  const [modelId, setModelId] = useState('');
  const [name, setName] = useState('');
  const [deleted, setDeleted] = useState(false);
  const debouncedName = useDebounce(name, 500);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [pendingStyleId, setPendingStyleId] = useState('');
  const [deleteStyle] = useDeleteStyleMutation();
  const [restoreStyle] = useRestoreStyleMutation();
  useDocumentTitle('Styles');
  const filters = {
    toolId: toolId || undefined,
    modelId: modelId || undefined,
    name: debouncedName || undefined,
    deleted: deleted || undefined,
  };
  const { page, size, setPage, setSize } = usePagedList(filters);
  const { data, isLoading } = useGetAllStylesQuery({ page, size, ...filters });
  const { data: models } = useGetAllModelsQuery({ page: 0, size: MODELS_FILTER_SIZE });
  const openFormHandler = (style?: StyleListItemType) =>
    navigate(style ? routes.pages.updateStylePageById(style.id) : routes.pages.newStylePage());
  const deleteStyleHandler = async (style: StyleListItemType) => {
    setPendingStyleId(style.id);
    try {
      await deleteStyle(style.id).unwrap();
      toast.success('Style deleted');
    } catch (error) {
      if (isFetchBaseQueryError(error) && error.status === 403) toast.error('Access denied');
      else toast.error('Failed to delete the style');
    } finally {
      setPendingStyleId('');
    }
  };
  const restoreStyleHandler = async (style: StyleListItemType) => {
    setPendingStyleId(style.id);
    try {
      await restoreStyle(style.id).unwrap();
      toast.success('Style restored');
    } catch (error) {
      if (isFetchBaseQueryError(error) && error.status === 403) toast.error('Access denied');
      else toast.error('Failed to restore the style');
    } finally {
      setPendingStyleId('');
    }
  };
  const confirmDeleteHandler = (style: StyleListItemType) =>
    dispatch(
      openModal({
        type: 'actionWithTool',
        extra: {
          id: style.id,
          title: 'Delete style?',
          supportText: 'The style',
          textInformation: style.name,
          subSupportText: 'will become unavailable to users',
          handler: () => deleteStyleHandler(style),
          actionButtonColor: 'danger',
          actionButtonText: 'Delete',
        },
      }),
    );
  const confirmRestoreHandler = (style: StyleListItemType) =>
    dispatch(
      openModal({
        type: 'actionWithTool',
        extra: {
          id: style.id,
          title: 'Restore style?',
          supportText: 'The style',
          textInformation: style.name,
          subSupportText: 'will become available to users again',
          handler: () => restoreStyleHandler(style),
          actionButtonColor: 'primary',
          actionButtonText: 'Restore',
        },
      }),
    );
  const actionsOf = (style: StyleListItemType): RowAction[] =>
    style.deleted
      ? [
          {
            label: 'Restore',
            onSelect: () => confirmRestoreHandler(style),
            disabled: pendingStyleId === style.id,
          },
        ]
      : [
          { label: 'Edit', onSelect: () => openFormHandler(style) },
          {
            label: 'Delete',
            onSelect: () => confirmDeleteHandler(style),
            danger: true,
            disabled: pendingStyleId === style.id,
          },
        ];
  const columns: DataTableColumn<StyleListItemType>[] = [
    { key: 'name', title: 'Name', render: (style) => style.name },
    {
      key: 'toolId',
      title: 'Tool',
      width: 'minmax(110px, 0.8fr)',
      render: (style) => (style.toolId ? formatToolIdName(style.toolId) : DASH),
    },
    { key: 'modelName', title: 'Model', render: (style) => style.modelName || DASH },
    {
      key: 'providerId',
      title: 'Provider',
      width: 'minmax(110px, 0.8fr)',
      render: (style) => (style.providerId ? formatAiProviderName(style.providerId) : DASH),
    },
    {
      key: 'version',
      title: 'Version',
      width: 'minmax(90px, 0.5fr)',
      render: (style) => formatSnapshotVersion(style),
    },
    {
      key: 'prompt',
      title: 'Prompt',
      width: 'minmax(200px, 2fr)',
      render: (style) => <span title={style.prompt}>{style.prompt || DASH}</span>,
    },
    {
      key: 'deleted',
      title: 'Status',
      width: 'minmax(90px, 0.5fr)',
      render: (style) => (
        <span className={style.deleted ? cls.Deleted : cls.Active}>
          {style.deleted ? 'Deleted' : 'Active'}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: '56px',
      allowOverflow: true,
      render: (style) => <RowActionsDropdown id={style.id} actions={actionsOf(style)} />,
    },
  ];
  return (
    <div className={cls.Container}>
      <div className={cls.Filters}>
        <Form.Select
          value={toolId}
          onChange={(event) => setToolId(event.target.value)}
          className={cls.Filter}
          aria-label="Filter by tool"
        >
          <option value="">All tools</option>
          {TOOL_IDS.map((id) => (
            <option key={id} value={id}>
              {formatToolIdName(id)}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          value={modelId}
          onChange={(event) => setModelId(event.target.value)}
          className={cls.Filter}
          aria-label="Filter by model"
        >
          <option value="">All models</option>
          {models?.models.map((model) => (
            <option key={model.id} value={model.id}>
              {model.name}
            </option>
          ))}
        </Form.Select>
        <Form.Control
          type="text"
          value={name}
          placeholder="Search by name"
          onChange={(event) => setName(event.target.value)}
          className={cls.Filter}
        />
        <Form.Check
          type="switch"
          id="styles-deleted-filter"
          label="Deleted only"
          checked={deleted}
          onChange={(event) => setDeleted(event.target.checked)}
        />
        <Button className={cls.CreateButton} onClick={() => openFormHandler()}>
          Add style
        </Button>
      </div>
      <DataTable
        columns={columns}
        items={data?.styles}
        isLoading={isLoading}
        emptyText="No styles found"
        rowKey={(style) => style.id}
        onRowClick={(style) => navigate(routes.pages.stylePageById(style.id))}
        footer={
          data && (
            <PaginationBar
              page={page}
              size={size}
              countAll={data.countAll}
              onPageChange={setPage}
              onSizeChange={setSize}
              itemsName="styles"
            />
          )
        }
      />
    </div>
  );
};

export default StylesTab;
